import type {
  CalendarDaySummary,
  CalendarEntry,
  MonthCell,
  WeekDay,
} from '../../api/calendarTypes.js';
import type { MealSlot } from '../../api/calendarTypes.js';

export function emptySummary(): CalendarDaySummary {
  return { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };
}

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map((s) => Number.parseInt(s, 10));
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function addDays(iso: string, n: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + n);
  return toIsoDate(d);
}

export function diffDays(a: string, b: string): number {
  const aD = parseIsoDate(a).getTime();
  const bD = parseIsoDate(b).getTime();
  return Math.round((aD - bD) / 86400000);
}

export function todayIso(): string {
  return toIsoDate(new Date());
}

export function startOfWeek(iso: string, weekStartsOn = 1): string {
  const d = parseIsoDate(iso);
  const day = d.getDay();
  const diff = (day - weekStartsOn + 7) % 7;
  d.setDate(d.getDate() - diff);
  return toIsoDate(d);
}

export function weekDaysOf(iso: string, weekStartsOn = 1): WeekDay[] {
  const start = startOfWeek(iso, weekStartsOn);
  const today = todayIso();
  const weekdayFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
  const days: WeekDay[] = [];
  for (let i = 0; i < 7; i += 1) {
    const dIso = addDays(start, i);
    const d = parseIsoDate(dIso);
    days.push({
      iso: dIso,
      weekdayShort: weekdayFmt.format(d),
      dayOfMonth: d.getDate(),
      isToday: dIso === today,
    });
  }
  return days;
}

export function formatLongDate(iso: string): string {
  const d = parseIsoDate(iso);
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

export function formatWeekdayShort(iso: string): string {
  const d = parseIsoDate(iso);
  return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(d);
}

export function summarizeDay(
  entries: ReadonlyArray<CalendarEntry>,
  isoDate: string,
): CalendarDaySummary {
  const acc = emptySummary();
  for (const e of entries) {
    if (e.date !== isoDate) continue;
    acc.kcal += e.total_kcal;
    acc.protein_g += e.total_protein;
    acc.carbs_g += e.total_carbs;
    acc.fat_g += e.total_fat;
  }
  return acc;
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n);
}

export interface MonthWindow {
  firstIso: string;
  lastIso: string;
  cells: MonthCell[];
  monthStart: Date;
  monthEnd: Date;
}

export function monthWindowOf(iso: string, weekStartsOn = 1): MonthWindow {
  const d = parseIsoDate(iso);
  const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
  const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const gridStart = parseIsoDate(startOfWeek(toIsoDate(monthStart), weekStartsOn));
  const gridEnd = parseIsoDate(startOfWeek(toIsoDate(monthEnd), weekStartsOn));
  gridEnd.setDate(gridEnd.getDate() + 6);

  const today = todayIso();
  const cells: MonthCell[] = [];
  for (
    let cursor = new Date(gridStart);
    cursor.getTime() <= gridEnd.getTime();
    cursor.setDate(cursor.getDate() + 1)
  ) {
    const cIso = toIsoDate(cursor);
    const inMonth = cursor.getMonth() === monthStart.getMonth();
    cells.push({
      iso: cIso,
      dayOfMonth: cursor.getDate(),
      inMonth,
      isToday: cIso === today,
      isSelected: false,
      entries: [],
    });
  }

  return {
    firstIso: toIsoDate(monthStart),
    lastIso: toIsoDate(monthEnd),
    cells,
    monthStart,
    monthEnd,
  };
}

export function monthLabelOf(iso: string): string {
  const d = parseIsoDate(iso);
  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
  }).format(d);
}

export function weekdayHeadings(weekStartsOn = 1): string[] {
  const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
  const base = new Date(2024, 0, 7);
  const out: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(base);
    d.setDate(base.getDate() + ((i + weekStartsOn) % 7));
    out.push(fmt.format(d));
  }
  return out;
}

export function shiftMonth(iso: string, delta: number): string {
  const d = parseIsoDate(iso);
  const target = new Date(d.getFullYear(), d.getMonth() + delta, 1);
  return toIsoDate(target);
}

export function entriesByDate(
  entries: ReadonlyArray<CalendarEntry>,
): Map<string, CalendarEntry[]> {
  const map = new Map<string, CalendarEntry[]>();
  for (const e of entries) {
    const arr = map.get(e.date) ?? [];
    arr.push(e);
    map.set(e.date, arr);
  }
  return map;
}

export function slotEntriesByDate(
  entries: ReadonlyArray<CalendarEntry>,
  isoDate: string,
): Map<MealSlot, CalendarEntry[]> {
  const map = new Map<MealSlot, CalendarEntry[]>();
  for (const e of entries) {
    if (e.date !== isoDate) continue;
    const arr = map.get(e.meal_type) ?? [];
    arr.push(e);
    map.set(e.meal_type, arr);
  }
  return map;
}
